import {
  ActionIcon,
  Alert,
  Anchor,
  Badge,
  Button,
  Card,
  Center,
  Checkbox,
  Divider,
  FileInput,
  Group,
  Modal,
  SegmentedControl,
  Select,
  Stack,
  Switch,
  TagsInput,
  Text,
  TextInput,
  Textarea,
  Tooltip,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import {
  IconFileText,
  IconFilter,
  IconFlag,
  IconMessage,
  IconPencil,
  IconPlus,
  IconSearch,
  IconSparkles,
  IconTag,
  IconUpload,
  IconWorld,
  IconX,
} from '@tabler/icons-react';
import ISO6391 from 'iso-639-1';
import queryString from 'query-string';
import { useCallback, useEffect, useRef, useState } from 'react';
import useFetch from '../../helpers/useFetch';
import useUploadAsset from '../../helpers/useUploadAsset';
import AssetPickerModal from '../Assets/AssetPickerModal';
import Spinner from '../Spinner';
import classes from './DocumentsScreen.module.css';
import {
  PaginationBar,
  SOURCE_TYPE_LABELS,
  STAGE_COLORS,
  StatusBadge,
  TitleEditor,
  TitleLockIcon,
  formatDate,
  interpretIngestError,
  stripProtocol,
} from './shared';

const COL = {
  check: { width: 44, flex: '0 0 auto' },
  doc: { flex: '1.9 1 0%', minWidth: 206 },
  locale: { width: 52, flex: '0 0 auto' },
  tags: { width: 148, flex: '0 0 auto' },
  units: { width: 108, flex: '0 0 auto' },
  ingest: { width: 88, flex: '0 0 auto' },
  updated: { width: 82, flex: '0 0 auto', textAlign: 'right' },
};

const ROW_STYLE = { display: 'flex', alignItems: 'center', gap: 8 };

const SourceIcon = ({ sourceType }) => {
  const props = { size: 16, stroke: 1.5, color: 'var(--mantine-color-gray-6)' };
  if (sourceType === 'pdf') return <IconFileText {...props} />;
  if (sourceType === 'manual') return <IconPencil {...props} />;
  if (sourceType === 'chatwoot') return <IconMessage {...props} />;
  return <IconWorld {...props} />;
};

const jobTip = (job) => {
  // Merchant-friendly summary only — the raw error is superadmin-gated in Details.
  if (job.stage === 'failed') return interpretIngestError(job.error).title;
  if (job.stage === 'ready') return 'Last pipeline run finished successfully.';
  return 'Pipeline running — the previous version stays live meanwhile.';
};

const windowLabel = (doc) => {
  const fmt = (value) => new Date(value).toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
  if (doc.activeFrom && doc.activeTo) return `active ${fmt(doc.activeFrom)} – ${fmt(doc.activeTo)}`;
  if (doc.activeFrom) return `active from ${fmt(doc.activeFrom)}`;
  return `active until ${fmt(doc.activeTo)}`;
};

// A document with no locale serves every shop language. The create body can say so
// literally (`locale: null`); a query string can't carry null, so the list filter
// sends this sentinel instead — the API maps `locale=all-locales` to "locale IS NULL".
// Two spellings of one concept: keep both in step if either side changes.
const ALL_LOCALES_FORM_VALUE = '__all__';
const ALL_LOCALES_FILTER_VALUE = 'all-locales';

const makeForm = (defaultLocale) => ({
  sourceType: 'url',
  sourceUrl: '',
  title: '',
  content: '',
  locale: defaultLocale || 'bg',
  market: '',
  tags: [],
  enrich: false,
  aiStructure: false,
  reviewStatus: 'draft',
  pdfMode: 'upload',
  fileName: null,
  autoTitle: '',
});

const plural = (count) => `${count} document${count === 1 ? '' : 's'}`;

const withoutExtension = (name) => (name || '').replace(/\.[a-z0-9]{2,5}$/i, '');

// Uploaded assets get ` ~abcd` appended (titles are unique per domain); drop it again for the KB title.
const UPLOAD_SUFFIX = / ~[a-z0-9]{4}$/;
const fileTitle = (name) => withoutExtension(name).replace(UPLOAD_SUFFIX, '');

// The API's documentIds cap for bulkDelete.
const BULK_DELETE_LIMIT = 200;

const isPdfAsset = (asset) => asset.type === 'application/pdf';

// Same language source as the per-locale message/title editors (iso-639-1) — the
// knowledge screens must not invent their own shortlist.
const ALL_LANGUAGES = ISO6391.getLanguages(ISO6391.getAllCodes())
  .map((language) => ({ value: language.code, label: `${language.name} (${language.code})` }))
  .sort((a, b) => a.label.localeCompare(b.label));

const DocumentsScreen = ({ apiBase, scope, defaultLocale, refreshCounts, onOpenDocument }) => {
  const fetch = useFetch();

  const [reviewStatus, setReviewStatus] = useState('all');
  const [sourceType, setSourceType] = useState('all');
  const [locale, setLocale] = useState('all');
  const [tag, setTag] = useState('all');
  const [q, setQ] = useState('');
  const [qInput, setQInput] = useState('');
  // The filter selects stay collapsed behind the Filters button; they re-appear on
  // their own while any of them is actually narrowing the list.
  const [showFilters, setShowFilters] = useState(false);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [refreshKey, setRefreshKey] = useState(0);
  const [documents, setDocuments] = useState(null);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  const [selectedIds, setSelectedIds] = useState([]);
  const [selectedTag, setSelectedTag] = useState(null);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [confirmBulk, setConfirmBulk] = useState(null);

  const [modalOpen, setModalOpen] = useState(false);
  const [form, setForm] = useState(() => makeForm(defaultLocale));
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState(null);
  const [duplicateId, setDuplicateId] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  // Bumped whenever the form's source changes, so a late upload can't land in a different form.
  const uploadToken = useRef(0);
  // Unit counts of every row seen, so the publish confirm also covers rows on other pages.
  const unitCounts = useRef({});

  const onUploadError = useCallback((error) => setCreateError(error.message), []);
  const { uploadDocument } = useUploadAsset(onUploadError);

  useEffect(() => {
    setPage(1);
    setSelectedIds([]);
    setSelectedTag(null);
  }, [apiBase]);

  // A bulk action applies to the ids/tag held in state, which can outlive the rows that
  // carried them: check three rows, narrow the Status filter, and the toolbar would still
  // act on documents no longer on screen. Cross-page selection is deliberate; selection
  // across a *different set of rows* isn't.
  useEffect(() => {
    setSelectedIds([]);
    setSelectedTag(null);
  }, [apiBase, reviewStatus, sourceType, locale, tag, q]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setQ(qInput);
      setPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [qInput]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const all = (value) => (value === 'all' ? '' : value);
    const query = queryString.stringify({
      page,
      perPage,
      reviewStatus: all(reviewStatus),
      sourceType: all(sourceType),
      locale: all(locale),
      tag: all(tag),
      q,
    });
    fetch(`${apiBase}/documents?${query}`)
      .then((response) => {
        if (cancelled) return;
        // A bulk action or filter change can empty the current page — snap back.
        if (!response.documents.length && response.total > 0 && page > 1) {
          setPage(1);
          return;
        }
        response.documents.forEach((doc) => {
          unitCounts.current[doc.id] = doc.unitCount;
        });
        setDocuments(response.documents);
        setTotal(response.total);
      })
      .catch((error) => {
        if (cancelled) return;
        notifications.show({ title: 'Failed to load documents', message: error.message, color: 'danger' });
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [fetch, apiBase, page, perPage, reviewStatus, sourceType, locale, tag, q, refreshKey]);

  const visibleTags = [...new Set((documents || []).flatMap((doc) => doc.tags || []))].sort();
  const tagFilterOptions = tag !== 'all' && !visibleTags.includes(tag) ? [tag, ...visibleTags] : visibleTags;
  const tagPickOptions =
    selectedTag && !visibleTags.includes(selectedTag) ? [selectedTag, ...visibleTags] : visibleTags;

  const changeFilter = (setter) => (value) => {
    setter(value || 'all');
    setPage(1);
  };

  const isChecked = (doc) => (selectedTag ? (doc.tags || []).includes(selectedTag) : selectedIds.includes(doc.id));

  const toggleSelect = (docId) => {
    setSelectedTag(null);
    setSelectedIds((prev) => (prev.includes(docId) ? prev.filter((id) => id !== docId) : [...prev, docId]));
  };

  const visibleIds = (documents || []).map((doc) => doc.id);
  const visibleSelected = selectedTag ? [] : visibleIds.filter((id) => selectedIds.includes(id));

  const toggleSelectVisible = (checked) => {
    setSelectedTag(null);
    setSelectedIds((prev) =>
      checked ? [...new Set([...prev, ...visibleIds])] : prev.filter((id) => !visibleIds.includes(id))
    );
  };

  const clearSelection = () => {
    setSelectedIds([]);
    setSelectedTag(null);
  };

  const afterStatusChange = () => {
    clearSelection();
    setRefreshKey((key) => key + 1);
    refreshCounts();
  };

  // `title` names the toast from the updated count; publish/unpublish also clear the selection.
  const applyBulk = (set, message, { title, clear } = {}) => {
    const body = selectedTag ? { tag: selectedTag, set } : { documentIds: selectedIds, set };
    setBulkBusy(true);
    fetch(`${apiBase}/documents/bulk`, { method: 'PUT', body: JSON.stringify(body) })
      .then(({ updated }) => {
        notifications.show({
          title: title ? title(updated) : `${plural(updated)} updated`,
          message,
          color: 'success',
        });
        if (clear) afterStatusChange();
        else setRefreshKey((key) => key + 1);
      })
      .catch((error) => notifications.show({ title: 'Bulk update failed', message: error.message, color: 'danger' }))
      .finally(() => {
        setBulkBusy(false);
        setConfirmBulk(null);
      });
  };

  const tagNote = () =>
    selectedTag ? ` This applies to every document tagged “${selectedTag}” across all pages.` : '';

  // A tag scope mutates every document with the tag across all pages — far beyond the
  // rows shown checked — so confirm it. Explicit id selections apply directly.
  const bulkUpdate = (set, message, options) => {
    if (!selectedTag) {
      applyBulk(set, message, options);
      return;
    }
    setConfirmBulk({
      title: 'Apply to every document with this tag?',
      body: `This applies to every document tagged “${selectedTag}” across all pages — not only the rows shown checked. ${message}`,
      cta: 'Apply to all',
      run: () => applyBulk(set, message, options),
    });
  };

  const askPublish = () => {
    const count = selectedIds.length;
    // For a tag scope only the rows on screen are known.
    const empty = selectedTag
      ? (documents || []).filter((doc) => isChecked(doc) && doc.unitCount === 0).length
      : selectedIds.filter((id) => unitCounts.current[id] === 0).length;
    setConfirmBulk({
      title: selectedTag ? `Publish every document tagged “${selectedTag}”?` : `Publish ${plural(count)}?`,
      body: `They become available to the chat agent right away.${tagNote()}`,
      warning: empty
        ? `${plural(empty)} ${empty === 1 ? 'has' : 'have'} no units yet, so ${empty === 1 ? 'it' : 'they'} won’t answer anything until ingest succeeds.`
        : null,
      cta: selectedTag ? 'Publish all' : `Publish ${count}`,
      run: () =>
        applyBulk({ reviewStatus: 'published' }, undefined, {
          title: (updated) => `Published ${plural(updated)}`,
          clear: true,
        }),
    });
  };

  const unpublish = () =>
    bulkUpdate({ reviewStatus: 'draft' }, 'Moved back to draft — no longer served.', {
      title: (updated) => `Unpublished ${plural(updated)}`,
      clear: true,
    });

  const runDelete = () => {
    setBulkBusy(true);
    fetch(`${apiBase}/documents/bulkDelete`, { method: 'POST', body: JSON.stringify({ documentIds: selectedIds }) })
      .then(({ deleted, failed = [] }) => {
        notifications.show({
          title: `Deleted ${plural(deleted)}`,
          message: failed.length
            ? `${failed.length} failed: ${failed.map((failure) => failure.message).join('; ')}`
            : undefined,
          color: failed.length ? 'warning' : 'success',
        });
        afterStatusChange();
      })
      .catch((error) => notifications.show({ title: 'Bulk delete failed', message: error.message, color: 'danger' }))
      .finally(() => {
        setBulkBusy(false);
        setConfirmBulk(null);
      });
  };

  const askDelete = () =>
    setConfirmBulk({
      title: `Delete ${plural(selectedIds.length)}?`,
      body: 'This also deletes their units, versions and Q&A pairs, and can’t be undone.',
      cta: `Delete ${selectedIds.length}`,
      color: 'danger',
      run: runDelete,
    });

  const renameDocument = (docId, title) =>
    fetch(`${apiBase}/documents/${docId}`, { method: 'PUT', body: JSON.stringify({ title }) })
      .then(({ document }) => {
        setDocuments((prev) => prev.map((doc) => (doc.id === docId ? { ...doc, ...document } : doc)));
        setEditingId(null);
      })
      .catch((error) => notifications.show({ title: 'Rename failed', message: error.message, color: 'danger' }));

  const dropUpload = () => {
    uploadToken.current += 1;
    setUploading(false);
  };

  const openNewDocument = () => {
    dropUpload();
    setForm(makeForm(defaultLocale));
    setCreateError(null);
    setDuplicateId(null);
    setModalOpen(true);
  };

  const updateForm = (patch) => {
    setCreateError(null);
    setDuplicateId(null);
    setForm((prev) => ({ ...prev, ...patch }));
  };

  // A file-name title keeps the source recognisable; it replaces only an empty or prefilled title.
  const takeFile = (sourceUrl, fileName) => {
    setCreateError(null);
    setDuplicateId(null);
    setForm((prev) => {
      const autoTitle = fileTitle(fileName);
      const title = !prev.title || prev.title === prev.autoTitle ? autoTitle : prev.title;
      return { ...prev, sourceUrl, fileName, title, autoTitle };
    });
  };

  // Switching source drops the old source (and its prefilled title), which the new mode may not show.
  const resetSource = (patch) => {
    dropUpload();
    updateForm(patch);
    setForm((prev) => ({
      ...prev,
      sourceUrl: '',
      fileName: null,
      title: prev.title === prev.autoTitle ? '' : prev.title,
      autoTitle: '',
    }));
  };

  const uploadFile = (file) => {
    if (!file) return;
    if (file.type !== 'application/pdf') {
      updateForm({ sourceUrl: '', fileName: null });
      setCreateError('Choose a PDF file.');
      return;
    }
    updateForm({ sourceUrl: '', fileName: null });
    setUploading(true);
    const token = ++uploadToken.current;
    const suffix = Math.random().toString(36).slice(2, 6).padEnd(4, '0');
    uploadDocument({ title: `${withoutExtension(file.name)} ~${suffix}.pdf`, description: '' }, file)
      .then((url) => url && token === uploadToken.current && takeFile(url, file.name))
      .finally(() => token === uploadToken.current && setUploading(false));
  };

  // Uploading and picking need a domain's asset library, which the system corpus has none of.
  const fileSources = scope !== 'system';
  const pdfMode = fileSources ? form.pdfMode : 'url';

  const createDisabled =
    form.sourceType === 'manual' ? !form.title.trim() || !form.content.trim() : !form.sourceUrl.trim();

  const createDocument = () => {
    const body = {
      sourceType: form.sourceType,
      locale: form.locale === ALL_LOCALES_FORM_VALUE ? null : form.locale,
      reviewStatus: form.reviewStatus,
      enrich: form.enrich,
      ...(form.aiStructure && { processingMethod: 'llm_structured' }),
    };
    if (form.title.trim()) body.title = form.title.trim();
    if (form.sourceType === 'manual') body.content = form.content;
    else body.sourceUrl = form.sourceUrl.trim();
    if (form.market.trim()) body.market = form.market.trim();
    if (form.tags.length) body.tags = form.tags;

    setCreating(true);
    fetch(`${apiBase}/documents`, { method: 'POST', body: JSON.stringify(body) })
      .then(() => {
        setModalOpen(false);
        notifications.show({ title: 'Document created', message: 'Processing queued.', color: 'success' });
        setRefreshKey((key) => key + 1);
        refreshCounts();
      })
      .catch((error) => {
        if (error.documentId) {
          setDuplicateId(error.documentId);
          setCreateError('Already in the knowledge base.');
        } else {
          setCreateError(error.message || 'Failed to create the document.');
        }
      })
      .finally(() => setCreating(false));
  };

  // The system corpus serves 'en' only (the API 400s anything else).
  const localeOptions =
    scope === 'system'
      ? ALL_LANGUAGES.filter((language) => language.value === 'en')
      : [
          ...ALL_LANGUAGES.filter((language) => language.value === defaultLocale),
          ...ALL_LANGUAGES.filter((language) => language.value !== defaultLocale),
        ];
  const hasSelection = selectedIds.length > 0 || selectedTag !== null;
  const activeFilterCount = [reviewStatus, sourceType, locale, tag].filter((value) => value !== 'all').length;
  // The row shows while it's open or while it's narrowing the list — but then collapsing
  // has to clear the filters too, or the Filters button would look broken (it would toggle
  // state nothing on screen reflects) and the grid would stay narrowed for no visible reason.
  const filtersVisible = showFilters || activeFilterCount > 0;

  const toggleFilters = () => {
    if (filtersVisible) {
      setReviewStatus('all');
      setSourceType('all');
      setLocale('all');
      setTag('all');
      setPage(1);
      setShowFilters(false);
    } else {
      setShowFilters(true);
    }
  };

  const headerCell = {
    fontSize: 11,
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '.04em',
    color: 'var(--mantine-color-gray-6)',
  };

  return (
    <div>
      <Group gap={8} wrap="wrap" align="flex-end">
        {filtersVisible && (
          <>
            <Select
              w={150}
              label="Status"
              value={reviewStatus}
              onChange={changeFilter(setReviewStatus)}
              data={[
                { value: 'all', label: 'All statuses' },
                { value: 'draft', label: 'draft' },
                { value: 'validated', label: 'validated' },
                { value: 'published', label: 'published' },
                { value: 'archived', label: 'archived' },
              ]}
            />
            <Select
              w={140}
              label="Source"
              value={sourceType}
              onChange={changeFilter(setSourceType)}
              data={[
                { value: 'all', label: 'All source types' },
                { value: 'url', label: SOURCE_TYPE_LABELS.url },
                { value: 'pdf', label: SOURCE_TYPE_LABELS.pdf },
                { value: 'manual', label: SOURCE_TYPE_LABELS.manual },
              ]}
            />
            <Select
              w={120}
              label="Locale"
              value={locale}
              searchable
              onChange={changeFilter(setLocale)}
              // The system corpus can't create all-locales documents (the create modal
              // withholds the option), so the filter would always come back empty there.
              data={[
                { value: 'all', label: 'Any locale' },
                ...localeOptions,
                ...(scope === 'system' ? [] : [{ value: ALL_LOCALES_FILTER_VALUE, label: 'All-locales docs' }]),
              ]}
            />
            <Select
              w={150}
              label="Tag"
              value={tag}
              onChange={changeFilter(setTag)}
              data={[{ value: 'all', label: 'All tags' }, ...tagFilterOptions]}
            />
          </>
        )}
        <div style={{ flex: 1 }} />
        {/* Neutral wording on purpose: what `q` matches server-side isn't settled, and
            "Search content…" promised full-text matching this screen can't guarantee. */}
        <TextInput
          w={240}
          aria-label="Search documents"
          placeholder="Search documents…"
          leftSection={<IconSearch size={14} stroke={1.5} />}
          value={qInput}
          onChange={(e) => setQInput(e.currentTarget.value)}
        />
        <Tooltip label={activeFilterCount ? 'Hide and clear the active filters' : 'Filter the documents'} withArrow>
          <Button variant="default" leftSection={<IconFilter size={15} stroke={1.5} />} onClick={toggleFilters}>
            Filters{activeFilterCount ? ` · ${activeFilterCount}` : ''}
          </Button>
        </Tooltip>
        <Button leftSection={<IconPlus size={15} stroke={2} />} onClick={openNewDocument}>
          New document
        </Button>
      </Group>

      {/* Sticky, so the bar stays in view when the list is scrolled to the ticked row. */}
      {hasSelection && (
        <div style={{ position: 'sticky', top: 0, zIndex: 3, background: 'var(--mantine-color-body)', paddingTop: 10 }}>
          <Group
            gap={8}
            wrap="wrap"
            px={12}
            py={8}
            style={{
              background: 'var(--mantine-primary-color-light)',
              border: '1px solid var(--mantine-primary-color-light-hover)',
              borderRadius: 'var(--mantine-radius-sm)',
            }}
          >
            <Text fz={13} fw={600} style={{ color: 'var(--mantine-primary-color-filled)' }}>
              {selectedTag
                ? `tag “${selectedTag}” — applies to every document with this tag`
                : `${selectedIds.length} selected`}
            </Text>
            <div style={{ width: 1, height: 18, background: 'var(--mantine-primary-color-light-hover)' }} />
            <Button size="xs" disabled={bulkBusy} onClick={askPublish}>
              {selectedTag ? 'Publish' : `Publish (${selectedIds.length})`}
            </Button>
            <Button size="xs" variant="default" disabled={bulkBusy} onClick={unpublish}>
              Unpublish
            </Button>
            <Tooltip
              label={
                selectedTag
                  ? 'Delete works on a selection, not on a tag'
                  : `Delete works on up to ${BULK_DELETE_LIMIT} documents at a time`
              }
              disabled={!selectedTag && selectedIds.length <= BULK_DELETE_LIMIT}
              withArrow
            >
              <Button
                size="xs"
                variant="outline"
                color="danger"
                disabled={bulkBusy || !!selectedTag || selectedIds.length > BULK_DELETE_LIMIT}
                onClick={askDelete}
              >
                {selectedTag ? 'Delete' : `Delete (${selectedIds.length})`}
              </Button>
            </Tooltip>
            <div style={{ width: 1, height: 18, background: 'var(--mantine-primary-color-light-hover)' }} />
            <Button
              size="xs"
              variant="default"
              disabled={bulkBusy}
              onClick={() => bulkUpdate({ agentEnabled: true }, 'Agent enabled — published units will be served.')}
            >
              Agent on
            </Button>
            <Button
              size="xs"
              variant="default"
              disabled={bulkBusy}
              onClick={() => bulkUpdate({ agentEnabled: false }, 'Agent disabled — units withheld from the assistant.')}
            >
              Agent off
            </Button>
            <Button
              size="xs"
              variant="default"
              disabled={bulkBusy}
              onClick={() => bulkUpdate({ autoResync: true }, 'Nightly auto-resync turned on.')}
            >
              Auto-resync on
            </Button>
            <Button
              size="xs"
              variant="default"
              disabled={bulkBusy}
              onClick={() => bulkUpdate({ autoResync: false }, 'Nightly auto-resync turned off.')}
            >
              Auto-resync off
            </Button>
            <Select
              size="xs"
              w={130}
              placeholder="Set priority…"
              value={null}
              disabled={bulkBusy}
              data={['0', '1', '2', '3'].map((value) => ({ value, label: `priority ${value}` }))}
              onChange={(value) => value && bulkUpdate({ priority: parseInt(value, 10) }, `Priority set to ${value}.`)}
            />
            <div style={{ flex: 1 }} />
            <Select
              size="xs"
              w={150}
              placeholder="Select by tag…"
              value={selectedTag}
              disabled={bulkBusy}
              data={tagPickOptions}
              onChange={(value) => {
                if (!value) return;
                setSelectedTag(value);
                setSelectedIds([]);
              }}
            />
            <ActionIcon variant="subtle" color="gray" size="sm" aria-label="Clear selection" onClick={clearSelection}>
              <IconX size={14} stroke={1.5} />
            </ActionIcon>
          </Group>
        </div>
      )}

      {loading || documents === null ? (
        <Spinner />
      ) : (
        <Card withBorder radius="sm" padding="xs" mt={14} style={{ overflowX: 'auto' }}>
          <div
            style={{
              ...ROW_STYLE,
              ...headerCell,
              padding: '6px 10px 8px',
              borderBottom: '1px solid var(--mantine-color-gray-2)',
            }}
          >
            <span style={COL.check}>
              <Checkbox
                aria-label="Select all visible documents"
                disabled={visibleIds.length === 0}
                checked={visibleIds.length > 0 && visibleSelected.length === visibleIds.length}
                indeterminate={visibleSelected.length > 0 && visibleSelected.length < visibleIds.length}
                onChange={(event) => toggleSelectVisible(event.currentTarget.checked)}
              />
            </span>
            <span style={COL.doc}>Document</span>
            <span style={COL.locale}>Locale</span>
            <span style={COL.tags}>Tags</span>
            <span style={COL.units}>
              <Tooltip
                label="Chunks and Q&A pairs extracted from the document. “Live” = published and currently served to the assistant."
                multiline
                maw={260}
                withArrow
              >
                <span style={{ cursor: 'help' }}>Units</span>
              </Tooltip>
            </span>
            <span style={COL.ingest}>
              <Tooltip
                label="Newest pipeline run. A failed run never takes content down — the previous version stays live."
                multiline
                maw={260}
                withArrow
              >
                <span style={{ cursor: 'help' }}>Last ingest</span>
              </Tooltip>
            </span>
            <span style={COL.updated}>Updated</span>
          </div>

          {documents.map((doc) => {
            const subline = [stripProtocol(doc.sourceUrl), doc.market ? `market: ${doc.market}` : null]
              .filter(Boolean)
              .join(' · ');
            return (
              <div
                key={doc.id}
                className={classes.row}
                style={{ ...ROW_STYLE, padding: '9px 10px', borderBottom: '1px solid var(--mantine-color-gray-1)' }}
              >
                <span style={{ ...COL.check, display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                  <Checkbox
                    checked={isChecked(doc)}
                    onChange={() => toggleSelect(doc.id)}
                    aria-label={`Select ${doc.title}`}
                  />
                  <SourceIcon sourceType={doc.sourceType} />
                </span>
                <div style={COL.doc}>
                  <Group gap={8} wrap="wrap">
                    {editingId === doc.id ? (
                      <TitleEditor
                        size="xs"
                        value={doc.title}
                        onSave={(title) => renameDocument(doc.id, title)}
                        onCancel={() => setEditingId(null)}
                      />
                    ) : (
                      <Group gap={4} wrap="nowrap">
                        <Anchor fz="sm" fw={600} component="button" onClick={() => onOpenDocument(doc.id)}>
                          {doc.title}
                        </Anchor>
                        {doc.titleLocked && <TitleLockIcon />}
                        <ActionIcon
                          className={classes.rename}
                          variant="subtle"
                          color="gray"
                          size="sm"
                          aria-label={`Rename ${doc.title}`}
                          onClick={() => setEditingId(doc.id)}
                        >
                          <IconPencil size={14} stroke={1.5} />
                        </ActionIcon>
                      </Group>
                    )}
                    <StatusBadge status={doc.reviewStatus} />
                    {doc.lastJob?.stage === 'failed' && (
                      <Tooltip
                        label={`${interpretIngestError(doc.lastJob.error).title} The previously indexed version stays live — the assistant keeps answering from it.`}
                        multiline
                        maw={280}
                        withArrow
                      >
                        <Badge variant="light" color="danger" size="sm">
                          ingest failed
                        </Badge>
                      </Tooltip>
                    )}
                    {!doc.agentEnabled && (
                      <Tooltip label="Withheld from the assistant even where published." multiline maw={260} withArrow>
                        <Badge variant="dot" color="gray" size="sm">
                          agent disabled
                        </Badge>
                      </Tooltip>
                    )}
                    {doc.priority > 0 && (
                      <Tooltip
                        label="Retrieval boost — priority (P1–P3) multiplies this document’s final ranking score."
                        multiline
                        maw={260}
                        withArrow
                      >
                        <Badge variant="default" size="sm" tt="none" leftSection={<IconFlag size={12} stroke={1.5} />}>
                          priority {doc.priority}
                        </Badge>
                      </Tooltip>
                    )}
                    {(doc.activeFrom || doc.activeTo) && (
                      <Tooltip
                        label="Only served inside this date window — e.g. seasonal campaign content."
                        multiline
                        maw={260}
                        withArrow
                      >
                        <Badge variant="default" size="sm" tt="none">
                          {windowLabel(doc)}
                        </Badge>
                      </Tooltip>
                    )}
                  </Group>
                  {subline && (
                    <Text
                      fz={12}
                      c="dimmed"
                      mt={2}
                      style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 560 }}
                    >
                      {subline}
                    </Text>
                  )}
                </div>
                <span style={COL.locale}>
                  <Badge variant="default" size="sm">
                    {doc.locale || 'all locales'}
                  </Badge>
                </span>
                <span style={{ ...COL.tags, display: 'inline-flex', gap: 4, flexWrap: 'wrap' }}>
                  {(doc.tags || []).map((docTag) => (
                    <Badge
                      key={docTag}
                      variant="default"
                      size="sm"
                      tt="none"
                      leftSection={<IconTag size={12} stroke={1.5} />}
                    >
                      {docTag}
                    </Badge>
                  ))}
                </span>
                <span style={{ ...COL.units, whiteSpace: 'nowrap' }}>
                  <Text span fz={13} fw={600}>
                    {doc.unitCount} {doc.unitCount === 1 ? 'unit' : 'units'}
                  </Text>
                  <Text span fz={13} c="dimmed">
                    {' '}
                    · {doc.publishedUnitCount} live
                  </Text>
                </span>
                <span style={COL.ingest}>
                  {doc.lastJob ? (
                    <Tooltip label={jobTip(doc.lastJob)} multiline maw={260} withArrow>
                      <Badge variant="light" color={STAGE_COLORS[doc.lastJob.stage] || 'gray'} size="sm">
                        {doc.lastJob.stage}
                      </Badge>
                    </Tooltip>
                  ) : (
                    <Text fz={12} c="dimmed">
                      —
                    </Text>
                  )}
                </span>
                <Text fz={12} c="dimmed" style={COL.updated}>
                  {formatDate(doc.updatedAt)}
                </Text>
              </div>
            );
          })}

          {documents.length === 0 && (
            <Center py={56}>
              <Stack align="center" gap={8}>
                <IconSearch size={44} stroke={1} color="var(--mantine-color-gray-5)" />
                <Text fz="lg" fw={500}>
                  No results found
                </Text>
                <Text size="sm" c="dimmed">
                  Try adjusting the filters or the search query.
                </Text>
              </Stack>
            </Center>
          )}
        </Card>
      )}

      {documents !== null && total > 0 && (
        <PaginationBar
          shown={documents.length}
          total={total}
          noun="documents"
          page={page}
          onPageChange={setPage}
          perPage={perPage}
          onPerPageChange={(value) => {
            setPerPage(value);
            setPage(1);
          }}
          mt={14}
        />
      )}

      <Modal
        opened={!!confirmBulk}
        onClose={() => !bulkBusy && setConfirmBulk(null)}
        title={
          <Text fw={600} c={confirmBulk?.color}>
            {confirmBulk?.title}
          </Text>
        }
        size="md"
        centered
      >
        <Text size="sm">{confirmBulk?.body}</Text>
        {confirmBulk?.warning && (
          <Alert color="warning" variant="light" mt="sm">
            {confirmBulk.warning}
          </Alert>
        )}
        <Divider my="md" />
        <Group justify="flex-end" gap={8}>
          <Button variant="default" disabled={bulkBusy} onClick={() => setConfirmBulk(null)}>
            Cancel
          </Button>
          <Button color={confirmBulk?.color} loading={bulkBusy} onClick={() => confirmBulk?.run()}>
            {confirmBulk?.cta}
          </Button>
        </Group>
      </Modal>

      <Modal opened={modalOpen} onClose={() => setModalOpen(false)} title="New document" size="lg" centered>
        <div style={{ display: 'grid', gridTemplateColumns: '140px 1fr', gap: '14px 12px', alignItems: 'center' }}>
          <Text size="sm" fw={500}>
            Source
          </Text>
          <div>
            <SegmentedControl
              value={form.sourceType}
              onChange={(value) => resetSource({ sourceType: value })}
              data={[
                { value: 'url', label: 'URL' },
                { value: 'pdf', label: 'PDF' },
                { value: 'manual', label: 'Manual text' },
              ]}
            />
          </div>
          {form.sourceType === 'pdf' && fileSources && (
            <>
              <Text size="sm" fw={500}>
                PDF
              </Text>
              <div>
                <SegmentedControl
                  value={form.pdfMode}
                  onChange={(value) => resetSource({ pdfMode: value })}
                  data={[
                    { value: 'upload', label: 'Upload file' },
                    { value: 'pick', label: 'Pick from Assets' },
                    { value: 'url', label: 'Paste URL' },
                  ]}
                />
              </div>
            </>
          )}
          {form.sourceType === 'pdf' && pdfMode !== 'url' && (
            <>
              <Text size="sm" fw={500}>
                File *
              </Text>
              <Group gap={8} wrap="wrap">
                {form.fileName && (
                  <Badge variant="default" size="lg" tt="none" leftSection={<IconFileText size={14} stroke={1.5} />}>
                    {form.fileName}
                  </Badge>
                )}
                {pdfMode === 'upload' ? (
                  <FileInput
                    w={form.fileName ? 140 : 260}
                    accept="application/pdf"
                    aria-label="PDF file"
                    placeholder={form.fileName ? 'Change file…' : 'Choose a PDF…'}
                    leftSection={<IconUpload size={14} stroke={1.5} />}
                    value={null}
                    disabled={uploading}
                    onChange={uploadFile}
                  />
                ) : (
                  <Button variant="default" onClick={() => setPickerOpen(true)}>
                    {form.fileName ? 'Change' : 'Choose from Assets'}
                  </Button>
                )}
                {uploading && (
                  <Text fz={12} c="dimmed">
                    Uploading…
                  </Text>
                )}
              </Group>
            </>
          )}
          <Text size="sm" fw={500}>
            {form.sourceType === 'manual' ? 'Title *' : 'Title'}
          </Text>
          <TextInput
            placeholder={form.sourceType === 'manual' ? 'Document title' : 'Optional — defaults to the source’s title'}
            value={form.title}
            onChange={(e) => updateForm({ title: e.currentTarget.value })}
          />
          {form.sourceType === 'manual' ? (
            <>
              <Text size="sm" fw={500} style={{ alignSelf: 'start', paddingTop: 6 }}>
                Content *
              </Text>
              <Textarea
                placeholder="Markdown…"
                autosize
                minRows={5}
                value={form.content}
                onChange={(e) => updateForm({ content: e.currentTarget.value })}
              />
            </>
          ) : (
            (form.sourceType === 'url' || pdfMode === 'url') && (
              <>
                <Text size="sm" fw={500}>
                  {form.sourceType === 'pdf' ? 'PDF URL *' : 'Source URL *'}
                </Text>
                <TextInput
                  placeholder="https://…"
                  value={form.sourceUrl}
                  onChange={(e) => updateForm({ sourceUrl: e.currentTarget.value })}
                />
              </>
            )
          )}
          <Text size="sm" fw={500}>
            Locale
          </Text>
          <div>
            <Select
              w={140}
              data={
                scope === 'system'
                  ? localeOptions
                  : [{ value: ALL_LOCALES_FORM_VALUE, label: 'All locales' }, ...localeOptions]
              }
              value={form.locale}
              searchable
              onChange={(value) => value && updateForm({ locale: value })}
            />
            {form.locale === ALL_LOCALES_FORM_VALUE && (
              <Text fz={12} c="dimmed" mt={4}>
                Served to every shop language — leave Market empty to cover every market too.
              </Text>
            )}
          </div>
          <Text size="sm" fw={500}>
            Market
          </Text>
          <div>
            <TextInput
              w={220}
              placeholder="Optional"
              value={form.market}
              onChange={(e) => updateForm({ market: e.currentTarget.value })}
            />
            <Text fz={12} c="dimmed" mt={4}>
              Limits this document to one market.
            </Text>
          </div>
          <Text size="sm" fw={500}>
            Tags
          </Text>
          <TagsInput placeholder="Add tags…" value={form.tags} onChange={(value) => updateForm({ tags: value })} />
          <Text size="sm" fw={500}>
            AI processing
          </Text>
          <div
            style={{
              border: '1px solid var(--mantine-primary-color-light-hover)',
              background: 'var(--mantine-primary-color-light)',
              borderRadius: 'var(--mantine-radius-sm)',
              padding: '10px 12px',
            }}
          >
            <Switch
              size="sm"
              label={
                <Group gap={6} wrap="nowrap">
                  <IconSparkles size={14} stroke={1.5} />
                  <span>Structure content with AI</span>
                </Group>
              }
              checked={form.aiStructure}
              onChange={(e) => updateForm({ aiStructure: e.currentTarget.checked })}
            />
            <Text fz={12} c="dimmed" mt={4} mb={10}>
              Rewrites the extraction into clean, organized text before chunking — best for messy pages and PDFs.
              The result waits as a draft for your review before publishing; nightly auto-resync doesn&apos;t apply.
            </Text>
            <Switch
              size="sm"
              label={
                <Group gap={6} wrap="nowrap">
                  <IconSparkles size={14} stroke={1.5} />
                  <span>Generate Q&amp;A pairs with AI</span>
                </Group>
              }
              checked={form.enrich}
              onChange={(e) => updateForm({ enrich: e.currentTarget.checked })}
            />
            <Text fz={12} c="dimmed" mt={4}>
              Generated pairs wait as drafts in Q&amp;A Review — nothing goes live unapproved. Both options use AI
              tokens.
            </Text>
          </div>
          <Text size="sm" fw={500}>
            Status
          </Text>
          <div>
            <Select
              w={140}
              data={['draft', 'validated']}
              value={form.reviewStatus}
              onChange={(value) => value && updateForm({ reviewStatus: value })}
            />
          </div>
        </div>
        {createError && (
          <Alert color={duplicateId ? 'warning' : 'danger'} variant="light" mt="md">
            <Group gap={8} wrap="wrap">
              <span>{createError}</span>
              {duplicateId && (
                <Button
                  size="compact-sm"
                  variant="subtle"
                  onClick={() => {
                    setModalOpen(false);
                    onOpenDocument(duplicateId);
                  }}
                >
                  Open document
                </Button>
              )}
            </Group>
          </Alert>
        )}
        <Divider my="md" />
        <Group justify="flex-end" gap={8}>
          <Button variant="default" onClick={() => setModalOpen(false)}>
            Cancel
          </Button>
          <Button disabled={createDisabled || uploading} loading={creating} onClick={createDocument}>
            Create document
          </Button>
        </Group>
      </Modal>

      {fileSources && (
        <AssetPickerModal
          show={pickerOpen}
          tab="documents"
          title="Pick a document"
          assetFilter={isPdfAsset}
          zIndex={400}
          close={() => setPickerOpen(false)}
          confirm={(asset, siteUrl, close) => {
            takeFile(asset.url, asset.title);
            close();
          }}
        />
      )}
    </div>
  );
};

export default DocumentsScreen;
